

import React, { useState, useEffect, useRef } from "react";
import "./Newsuport.css";
import { 
  FaPhoneAlt, FaTimes, FaPaperPlane, FaChevronDown, FaChevronUp, 
  FaPlus, FaMinus, FaTicketAlt, FaComments, FaPaperclip, FaCheckCircle, 
  FaClock, FaExclamationCircle, FaQuestionCircle, FaListAlt, FaPlusCircle,
  FaEye
} from "react-icons/fa";
import SupportTickets from "../sidebarcall/SupportTickets.jsx";

const Support = ({ numbers = ['+919876543210'] }) => {
  // ==================== TAB STATE ====================
  const [activeTab, setActiveTab] = useState("faq"); // "faq", "raiseTicket", "myTickets"

  // ==================== FAQ STATES ====================
  const [expandedCategories, setExpandedCategories] = useState({});
  const [expandedQuestions, setExpandedQuestions] = useState({});

  // ==================== TICKET STATES ====================
  const [showTicketPopup, setShowTicketPopup] = useState(false);
  const [chatMessage, setChatMessage] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [ticketType, setTicketType] = useState("");
  const [tickets, setTickets] = useState([]);
  const [expandedTicketId, setExpandedTicketId] = useState(null); // For expanded view on card

  // ==================== CHAT STATES ====================
  const [showChatPopup, setShowChatPopup] = useState(false);
  const [messages, setMessages] = useState([
    { text: "Hello! Thank you for contacting support. How can I help you today?", type: "agent", time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }), timestamp: Date.now() },
  ]);
  const [inputMessage, setInputMessage] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [openCallDropdown, setOpenCallDropdown] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const containerRef = useRef(null);
  const chatEndRef = useRef(null);
  const fileInputRef = useRef(null);

  // ==================== MOBILE DETECTION ====================
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // ==================== FAQ CATEGORIES DATA ====================
  const faqCategories = [
    {
      id: "coupons",
      name: "Coupons & Offers",
      icon: "🎁",
      description: "Get help with discount codes and promotional offers",
      questions: [
        { question: "Coupon not working/expired coupon", answer: "Please check the coupon terms and validity before applying. If you're facing issues, ensure the coupon meets the minimum order value requirements and hasn't expired. For further assistance, please raise a ticket with the coupon code details." },
        { question: "I forgot to apply my coupon code. What do I do now?", answer: "Unfortunately, we can't apply coupons after order placement as our system processes orders immediately. Please ensure you apply coupons before completing your purchase. You can cancel the order within 30 minutes and place a new one with the coupon." },
        { question: "How do I refer my friend?", answer: "Go to the refer section in the app and follow the instructions. You'll find a unique referral code to share with friends. Both you and your friend will receive rewards when they make their first purchase." },
      ]
    },
    {
      id: "orders",
      name: "Orders & Products",
      icon: "📦",
      description: "Questions about your orders and products",
      questions: [
        { question: "Can I reschedule my order?", answer: "If you've already placed an order, you can cancel it and place a new one when available. Please note that cancellation is only possible within 30 minutes of order placement." },
        { question: "Can I edit my cart/add items?", answer: "We start packing your order right away to ensure fast delivery. So changes after placing the order aren't allowed. Please review your cart carefully before checkout." },
        { question: "Is there a minimum order value?", answer: "No, we do not have a minimum or maximum order value. You can order any quantity you need without restrictions." },
      ]
    },
    {
      id: "feedback",
      name: "Feedback & Suggestions",
      icon: "💬",
      description: "Share your feedback and suggestions with us",
      questions: [
        { question: "Out of stock?", answer: "We apologize for the inconvenience. Please check back later or try alternative products. We restock regularly and you can enable notifications for when your preferred items are available." },
        { question: "Packing feedback?", answer: "We value your feedback on our packaging. Please share specific details so we can improve. Your input helps us maintain quality and sustainability standards." },
        { question: "Any feedback/Review?", answer: "We'd love to hear from you! Please provide your feedback through our app or website. Your reviews help us serve you better and improve our services." },
      ]
    },
    {
      id: "delivery",
      name: "Delivery Issues",
      icon: "🚚",
      description: "Questions about delivery, tracking, and timing",
      questions: [
        { question: "Where is my order?", answer: "You can track your order in real-time from the 'My Orders' section. If you need immediate assistance, please share your order ID with our live chat support." },
        { question: "Delivery delayed?", answer: "We apologize for the delay! Weather, traffic, or high demand may affect delivery times. Your order is prioritized and will reach you soon." },
        { question: "Wrong item received", answer: "We're sorry for the mix-up! Please raise a ticket with photos of the received item and your order ID. We'll resolve this within 24 hours." },
      ]
    }
  ];

  // ==================== AUTO-SCROLL CHAT ====================
  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  // ==================== FETCH TICKETS ====================
  const fetchTickets = async () => {
    const userId = localStorage.getItem("userId");
    const token = localStorage.getItem("authToken");

    try {
      const res = await fetch(
        `https://backend.maamaas.com/food/api/tickets/user/tickets?userId=${userId}`,
        {
          method: "GET",
          headers: {
            Accept: "application/json",
            ...(token && { Authorization: `Bearer ${token}` }),
          },
        }
      );

      if (!res.ok) {
        const errText = await res.text();
        throw new Error(errText);
      }

      const data = await res.json();
      setTickets(data);
    } catch (err) {
      console.error("Error fetching tickets:", err.message);
    }
  };

  useEffect(() => {
    fetchTickets();
  }, []);

  // ==================== CLICK OUTSIDE FOR CALL DROPDOWN ====================
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpenCallDropdown(false);
        setFocusedIndex(-1);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  // ==================== KEYBOARD NAVIGATION ====================
  useEffect(() => {
    const onKey = (e) => {
      if (!openCallDropdown) return;
      if (e.key === "Escape") {
        setOpenCallDropdown(false);
        setFocusedIndex(-1);
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        setFocusedIndex((i) => Math.min(i + 1, numbers.length - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setFocusedIndex((i) => Math.max(i - 1, 0));
      } else if (e.key === "Enter" && focusedIndex >= 0) {
        simCall(numbers[focusedIndex]);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [openCallDropdown, focusedIndex, numbers]);

  // ==================== RESIZE HANDLER ====================
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // ==================== PREVENT BODY SCROLL ON POPUPS ====================
  useEffect(() => {
    if (showTicketPopup || showChatPopup) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => { document.body.style.overflow = "unset"; };
  }, [showTicketPopup, showChatPopup]);

  // ==================== HELPER FUNCTIONS ====================
  const simCall = (number) => {
    window.location.href = `tel:${number.replace(/[^0-9+]/g, "")}`;
  };

  const toggleCallDropdown = (e) => {
    e.stopPropagation();
    setOpenCallDropdown((prev) => !prev);
    if (!openCallDropdown) setFocusedIndex(0);
  };

  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => ({ ...prev, [categoryId]: !prev[categoryId] }));
  };

  const toggleQuestion = (categoryId, questionIndex) => {
    const questionId = `${categoryId}-${questionIndex}`;
    setExpandedQuestions(prev => ({ ...prev, [questionId]: !prev[questionId] }));
  };

  const getStatusBadge = (status) => {
    const statusLower = status?.toLowerCase() || "";
    if (statusLower === "open") return { class: "status-open", icon: <FaClock />, text: "Open" };
    if (statusLower === "in_progress") return { class: "status-progress", icon: <FaExclamationCircle />, text: "In Progress" };
    if (statusLower === "resolved" || statusLower === "closed") return { class: "status-resolved", icon: <FaCheckCircle />, text: "Resolved" };
    return { class: "status-open", icon: <FaClock />, text: status || "Open" };
  };

  // ==================== LIVE CHAT LOGIC ====================
  const getAgentReply = (message) => {
    const msg = message.toLowerCase();
    if (msg.includes("order") && (msg.includes("id") || msg.includes("number"))) {
      return "Great! Please share your order ID and I'll look it up for you right away.";
    }
    if (msg.includes("refund")) {
      return "Refund requests typically take 3-5 business days. I can help you initiate one - please provide your order ID.";
    }
    if (msg.includes("delivery") && msg.includes("late")) {
      return "I understand your concern about delivery. Please share your order ID so I can check the real-time status for you.";
    }
    if (msg.includes("coupon")) {
      return "Coupon issues? Please share the coupon code and your order details. I'll verify and help you apply it correctly for future orders.";
    }
    if (msg.includes("thank")) {
      return "You're very welcome! Is there anything else I can help you with today?";
    }
    if (msg.includes("hello") || msg.includes("hi")) {
      return "Hello! How can I assist you with your order today?";
    }
    return "Thank you for reaching out. Could you please provide more details about your issue? I'm here to help!";
  };

  const handleSendMessage = () => {
    if (inputMessage.trim() === "") return;

    const userMsg = {
      text: inputMessage,
      type: "user",
      time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
      timestamp: Date.now(),
    };

    setMessages((prev) => [...prev, userMsg]);
    const sentMessage = inputMessage;
    setInputMessage("");
    setIsTyping(true);

    setTimeout(() => {
      const replyText = getAgentReply(sentMessage);
      const agentMsg = {
        text: replyText,
        type: "agent",
        time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
        timestamp: Date.now(),
      };
      setMessages((prev) => [...prev, agentMsg]);
      setIsTyping(false);
    }, 800);
  };

  // ==================== TICKET SUBMISSION ====================
  const handleSendTicket = async () => {
    if (!ticketType || !chatMessage.trim()) {
      alert("Please fill required fields");
      return;
    }

    const userId = localStorage.getItem("userId");
    const token = localStorage.getItem("authToken");

    if (!userId) {
      alert("User not found. Please login again.");
      return;
    }

    const formData = new FormData();
    const ticketData = {
      userId: Number(userId),
      ticketType,
      status: "OPEN",
      message: chatMessage.trim(),
      attachmentUrl: "",
    };

    formData.append(
      "ticketData",
      new Blob([JSON.stringify(ticketData)], { type: "application/json" })
    );

    if (selectedFile) {
      formData.append("attachment", selectedFile);
    }

    try {
      const response = await fetch(
        "https://backend.maamaas.com/food/api/tickets/create",
        {
          method: "POST",
          headers: {
            ...(token && { Authorization: `Bearer ${token}` }),
          },
          body: formData,
        }
      );

      const text = await response.text();
      if (!response.ok) throw new Error(text);

      alert("Ticket created successfully!");
      setChatMessage("");
      setSelectedFile(null);
      setTicketType("");
      setShowTicketPopup(false);
      fetchTickets();
      setActiveTab("myTickets");
    } catch (err) {
      console.error("Error:", err);
      alert(err.message);
    }
  };

  // ==================== TOGGLE TICKET EXPAND ====================
  const toggleTicketExpand = (ticketId) => {
    setExpandedTicketId(expandedTicketId === ticketId ? null : ticketId);
  };

  // ==================== RENDER FAQ TAB ====================
  const renderFaqTab = () => (
    <div className="faqs-section">
      <div className="section-header">
        <h2 className="section-title">How can we help you?</h2>
        <p className="section-subtitle">Find answers to common questions or get in touch with our support team</p>
      </div>

      <div className="faq-categories-container">
        {faqCategories.map((category) => {
          const isExpanded = expandedCategories[category.id];
          return (
            <div key={category.id} className={`faq-category-card ${isExpanded ? "expanded" : ""}`}>
              <div className="category-header" onClick={() => toggleCategory(category.id)}>
                <div className="category-icon">{category.icon}</div>
                <div className="category-info">
                  <h3>{category.name}</h3>
                  <p>{category.description}</p>
                </div>
                <div className="category-toggle">{isExpanded ? <FaChevronUp /> : <FaChevronDown />}</div>
              </div>
              {isExpanded && (
                <div className="category-questions">
                  {category.questions.map((item, idx) => {
                    const qId = `${category.id}-${idx}`;
                    const isQExpanded = expandedQuestions[qId];
                    return (
                      <div key={qId} className="question-item">
                        <div className="question-header" onClick={() => toggleQuestion(category.id, idx)}>
                          <span className="question-text">{item.question}</span>
                          <span className="question-icon">{isQExpanded ? <FaMinus /> : <FaPlus />}</span>
                        </div>
                        {isQExpanded && (
                          <div className="answer-content">
                            <p>{item.answer}</p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* <div className="support-actions">
        <div className="action-buttons">
          <button className="btn-ticket" onClick={() => { setShowTicketPopup(true); }}>
            <FaTicketAlt /> Raise a Ticket
          </button>
          <button className="btn-chat" onClick={() => setShowChatPopup(true)}>
            <FaComments /> Live Chat
          </button>
        </div>
      </div> */}
    </div>
  );

  // ==================== RENDER RAISE TICKET TAB ====================
  const renderRaiseTicketTab = () => (
    <div className="raise-ticket-section">
      <div className="section-header">
        <h2 className="section-title">Raise a Support Ticket</h2>
        <p className="section-subtitle">Submit your issue and our team will get back to you within 24 hours</p>
      </div>

      <div className="ticket-form-card">
        <div className="form-group">
          <label>Ticket Type *</label>
          <select value={ticketType} onChange={(e) => setTicketType(e.target.value)}>
            <option value="">Select type</option>
            <option value="DELIVERY_ISSUE">Delivery Issue</option>
            <option value="PAYMENT_PROBLEM">Payment Problem</option>
            <option value="WRONG_ORDER">Wrong Order</option>
            <option value="SERVICE_QUALITY">Service Quality</option>
            <option value="OTHER">Other</option>
          </select>
        </div>

        <div className="form-group">
          <label>Message *</label>
          <textarea
            rows="5"
            value={chatMessage}
            onChange={(e) => setChatMessage(e.target.value)}
            placeholder="Please describe your issue in detail..."
          />
        </div>

        <div className="form-group">
          <label><FaPaperclip /> Attachment (Optional, max 5MB)</label>
          <input 
            type="file" 
            ref={fileInputRef} 
            onChange={(e) => setSelectedFile(e.target.files[0])} 
            accept="image/*,.pdf,.doc,.docx" 
          />
          {selectedFile && <small className="file-name">📎 {selectedFile.name} ({(selectedFile.size / 1024).toFixed(2)} KB)</small>}
        </div>

        <button 
          className="submit-ticket-btn" 
          onClick={handleSendTicket} 
          disabled={!ticketType || !chatMessage.trim()}
        >
          <FaPaperPlane /> Submit Ticket
        </button>
      </div>
    </div>
  );

  // ==================== RENDER MY TICKETS TAB (CARD VIEW WITH EXPAND) ====================
  const renderMyTicketsTab = () => (
    <div className="my-tickets-section">
      <SupportTickets/>
    </div>
  );

  // ==================== MAIN RENDER ====================
  return (
    <div className="support-container">
      <div className="support-main">
        {/* TABS HEADER */}
        <div className="tabs-header">
          <button 
            className={`tab-btn ${activeTab === "faq" ? "active" : ""}`}
            onClick={() => setActiveTab("faq")}
          >
            <FaQuestionCircle /> FAQ
          </button>
          <button 
            className={`tab-btn ${activeTab === "raiseTicket" ? "active" : ""}`}
            onClick={() => setActiveTab("raiseTicket")}
          >
            <FaPlusCircle /> Raise Ticket
          </button>
          <button 
            className={`tab-btn ${activeTab === "myTickets" ? "active" : ""}`}
            onClick={() => setActiveTab("myTickets")}
          >
            <FaListAlt /> My Tickets
          </button>
        </div>

        {/* TAB CONTENT */}
        <div className="tab-content">
          {activeTab === "faq" && renderFaqTab()}
          {activeTab === "raiseTicket" && renderRaiseTicketTab()}
          {activeTab === "myTickets" && renderMyTicketsTab()}
        </div>
      </div>

      {/* Raise Ticket Popup */}
      {showTicketPopup && (
        <div className="popup-overlay" onClick={() => setShowTicketPopup(false)}>
          <div className="popup-container ticket-popup" onClick={(e) => e.stopPropagation()}>
            <div className="popup-header">
              <h3><FaTicketAlt /> Raise Support Ticket</h3>
              <button className="popup-close" onClick={() => setShowTicketPopup(false)}><FaTimes /></button>
            </div>
            <div className="popup-body">
              <div className="form-group">
                <label>Ticket Type *</label>
                <select value={ticketType} onChange={(e) => setTicketType(e.target.value)}>
                  <option value="">Select type</option>
                  <option value="DELIVERY_ISSUE">Delivery Issue</option>
                  <option value="PAYMENT_PROBLEM">Payment Problem</option>
                  <option value="WRONG_ORDER">Wrong Order</option>
                  <option value="SERVICE_QUALITY">Service Quality</option>
                  <option value="OTHER">Other</option>
                </select>
              </div>
              <div className="form-group">
                <label>Message *</label>
                <textarea rows="4" value={chatMessage} onChange={(e) => setChatMessage(e.target.value)} placeholder="Please describe your issue in detail..." />
              </div>
              <div className="form-group">
                <label><FaPaperclip /> Attachment (Optional)</label>
                <input type="file" ref={fileInputRef} onChange={(e) => setSelectedFile(e.target.files[0])} accept="image/*,.pdf,.doc,.docx" />
                {selectedFile && <small>📎 {selectedFile.name}</small>}
              </div>
              <button className="submit-ticket-btn" onClick={handleSendTicket} disabled={!ticketType || !chatMessage.trim()}>
                Submit Ticket
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Live Chat Popup */}
      {showChatPopup && (
        <div className="popup-overlay" onClick={() => setShowChatPopup(false)}>
          <div className="popup-container chat-popup" onClick={(e) => e.stopPropagation()}>
            <div className="chat-header">
              <div className="chat-header-info">
                <span className="live-dot"></span>
                <h4>Live Support</h4>
                <span className="agent-name">Agent Online</span>
              </div>
              <div className="chat-actions">
                <div className="call-dropdown-wrapper" ref={containerRef}>
                  <button className="call-button" onClick={toggleCallDropdown}>
                    <FaPhoneAlt /> Call
                  </button>
                  {openCallDropdown && (
                    <div className="call-dropdown-menu">
                      {numbers.map((num, idx) => (
                        <button key={num} className={`call-option ${focusedIndex === idx ? "focused" : ""}`} onClick={() => simCall(num)}>
                          📞 {num}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <button className="popup-close" onClick={() => setShowChatPopup(false)}><FaTimes /></button>
              </div>
            </div>
            <div className="chat-messages-area">
              {messages.map((msg, idx) => (
                <div key={idx} className={`chat-message ${msg.type}`}>
                  <div className="message-bubble">
                    <p>{msg.text}</p>
                    <span className="message-time">{msg.time}</span>
                  </div>
                </div>
              ))}
              {isTyping && (
                <div className="chat-message agent">
                  <div className="message-bubble typing-indicator">
                    <span></span><span></span><span></span>
                  </div>
                </div>
              )}
              <div ref={chatEndRef} />
            </div>
            <div className="chat-input-area">
              <input
                type="text"
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSendMessage()}
                placeholder="Type your message..."
              />
              <button onClick={handleSendMessage} disabled={!inputMessage.trim()}>
                <FaPaperPlane />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Support;